/**
 * Product context provider — shared product page data for product blocks
 *
 * Also owns the selected-variant state. The variant picker (ProductDetails),
 * the sticky add-to-cart bar and the media gallery are three sibling blocks in
 * the block grid, so the selection has to live above all of them. It is mirrored
 * into the `?variant=` query string, which keeps a chosen variant across a
 * reload and makes a specific version of a product linkable — the same
 * behaviour the Remix storefront had.
 */

import { createContext, useCallback, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getProductVariants,
  type ProductPageData,
  type ProductVariant,
} from "@/types/product";

/** Query-string parameter carrying the selected variant id. */
export const VARIANT_QUERY_PARAM = "variant";

export interface ProductContextValue {
  productData: ProductPageData;
  /** Every variant of this product; empty when it is sold in a single version. */
  variants: ProductVariant[];
  /** True when the customer has to pick a version before adding to the cart. */
  requiresVariantSelection: boolean;
  /** The chosen variant, or null while nothing is chosen. */
  selectedVariant: ProductVariant | null;
  /** Selects a variant by id. Unknown ids are ignored. */
  selectVariant: (variantId: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ProductContext = createContext<ProductContextValue | null>(null);

interface ProductProviderProps {
  productData: ProductPageData;
  children: ReactNode;
}

export function ProductProvider({
  productData,
  children,
}: ProductProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const variants = useMemo(
    () => getProductVariants(productData),
    [productData]
  );

  // A stale/hand-edited `?variant=` (e.g. an id from another product) must not
  // count as a selection — otherwise add-to-cart would post a variant the
  // product doesn't have.
  const variantIdFromUrl = searchParams.get(VARIANT_QUERY_PARAM);
  const selectedVariant = useMemo(
    () => variants.find((variant) => variant.id === variantIdFromUrl) ?? null,
    [variants, variantIdFromUrl]
  );

  const selectVariant = useCallback(
    (variantId: string) => {
      if (!variants.some((variant) => variant.id === variantId)) return;
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set(VARIANT_QUERY_PARAM, variantId);
          return next;
        },
        // Picking a version is not a navigation step: it must not push a history
        // entry (Back would walk through every option) or jump the page.
        { replace: true, preventScrollReset: true }
      );
    },
    [variants, setSearchParams]
  );

  const value = useMemo<ProductContextValue>(
    () => ({
      productData,
      variants,
      requiresVariantSelection: variants.length > 0,
      selectedVariant,
      selectVariant,
    }),
    [productData, variants, selectedVariant, selectVariant]
  );

  return (
    <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
  );
}
